/**
 * Text utility functions for formatting and sanitizing text content
 */

/**
 * Strip HTML tags from a string and return plain text
 * @param html - HTML string to strip
 * @returns Plain text with HTML tags removed
 */
export function stripHtml(html: string): string {
  if (!html || typeof html !== 'string') return '';

  // Remove HTML tags and decode entities
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = div.textContent || div.innerText || '';

  // Clean up extra whitespace
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Parse tags from various input formats and return as comma-separated string
 * Handles arrays, JSON strings, and plain strings
 * @param value - Value to parse tags from
 * @returns Clean comma-separated string of tags
 */
export function parseTags(value: any): string {
  if (!value) return '';

  // If it's already an array, join with commas
  if (Array.isArray(value)) {
    return value
      .map(tag => String(tag).replace(/['"]/g, ''))
      .filter(tag => tag.trim() !== '')
      .join(', ');
  }

  // If it's a string that looks like JSON array
  if (typeof value === 'string') {
    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed
          .map(tag => String(tag).replace(/['"]/g, ''))
          .filter(tag => tag.trim() !== '')
          .join(', ');
      }
    } catch {
      // If JSON parse fails, check if it's a string representation of an array
      if (value.startsWith('[') && value.endsWith(']')) {
        // Remove brackets and split by comma
        const content = value.slice(1, -1);
        return content
          .split(',')
          .map(tag => tag.trim().replace(/['"]/g, ''))
          .filter(tag => tag !== '')
          .join(', ');
      }
    }

    // If it's a regular string, return as is (removing quotes if present)
    return value.replace(/['"]/g, '').trim();
  }

  // For any other type, convert to string
  return String(value);
}

/**
 * Format a value for display in history, handling HTML and tags appropriately
 * @param value - Value to format
 * @param fieldName - Name of the field to determine formatting
 * @returns Formatted string for display
 */
export function formatValueForHistory(value: any, fieldName?: string): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' && value.trim() === '') return '';

  // Handle description fields by stripping HTML
  if (fieldName === 'description' && typeof value === 'string') {
    return stripHtml(value);
  }

  // Handle tags fields by parsing and formatting
  if (fieldName === 'tags' || fieldName === 'etiquetas') {
    return parseTags(value);
  }

  // Handle arrays (including tags if field name wasn't caught above)
  if (Array.isArray(value)) {
    return parseTags(value);
  }

  // Handle objects
  if (typeof value === 'object') {
    // Handle category objects
    if (value.name) return value.name;
    if (value.label) return value.label;
    // Handle other objects
    return JSON.stringify(value);
  }

  // For strings that might contain HTML, do a basic check and strip if needed
  if (typeof value === 'string' && value.includes('<') && value.includes('>')) {
    return stripHtml(value);
  }

  return String(value);
}
